import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * MobileAuth — frictionless bridge between the mobile app and web.
 *
 * Usage (mobile app opens):
 *   https://yourapp.com/auth?token=BEARER_TOKEN
 *
 * The token is read once, immediately stripped from the URL (security),
 * then validated via /profiler-account/me. On success the user is
 * redirected to their role-appropriate destination with no login prompt.
 */
export default function MobileAuth() {
  const { loginWithToken, user } = useAuth();
  const navigate = useNavigate();
  const didRun = useRef(false);
  const [status, setStatus] = useState("Authenticating…");

  useEffect(() => {
    // Guard against React strict-mode double-invoke
    if (didRun.current) return;
    didRun.current = true;

    // If the user is already authenticated (e.g. page refresh), skip the flow
    if (user) {
      redirectByRole(user.role);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    // Immediately wipe the token from the URL — it must not linger in history
    window.history.replaceState(null, "", window.location.pathname);

    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    setStatus("Verifying your session…");

    loginWithToken(token).then((result) => {
      if (!result.success) {
        navigate("/login", { replace: true });
        return;
      }
      setStatus("Welcome back!");
      redirectByRole(result.role);
    });

    function redirectByRole(role) {
      if (role === "tenant") {
        navigate("/tenant", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-white overflow-hidden select-none">
      {/* Subtle radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 50% 40%, rgba(139,92,246,0.08) 0%, transparent 70%)",
        }}
      />

      <div className="relative flex flex-col items-center gap-8 px-8 text-center">
        {/* App icon */}
        <div className="w-20 h-20 rounded-2xl bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-200">
          <svg
            className="w-10 h-10 text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </div>

        {/* App name */}
        <div className="flex flex-col gap-1">
          <span className="text-2xl font-bold tracking-tight text-gray-900">
            Provider
          </span>
          <span className="text-sm text-gray-400 font-medium tracking-wide">
            Management Dashboard
          </span>
        </div>

        {/* Spinner */}
        <div className="relative w-12 h-12">
          {/* Track */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 48 48"
            fill="none">
            <circle cx="24" cy="24" r="20" stroke="#f3f4f6" strokeWidth="3.5" />
          </svg>
          {/* Spinner arc */}
          <svg
            className="absolute inset-0 w-full h-full animate-spin"
            viewBox="0 0 48 48"
            fill="none"
            style={{ animationDuration: "0.9s" }}>
            <circle
              cx="24"
              cy="24"
              r="20"
              stroke="#7c3aed"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeDasharray="30 96"
            />
          </svg>
        </div>

        {/* Status text */}
        <p className="text-sm text-gray-400 font-medium tracking-wide animate-pulse">
          {status}
        </p>
      </div>

      {/* Bottom safe-area spacer for mobile notch */}
      <div className="absolute bottom-6 left-0 right-0 flex justify-center">
        <span className="text-xs text-gray-300 tracking-widest font-medium">
          SECURE CONNECTION
        </span>
      </div>
    </div>
  );
}
